<template>
    <div :style="cssVars" :key="'organisation-'+dataBelongsTo+'-refresh-'+refreshKey+'-loggedIn-'+(loggedIn ? 'yes' : 'no')">
        <b-notification type="is-warning" role="alert" v-if="user && user.demo">
            <strong>Let op:</strong>&nbsp;Dit is een demo account.
        </b-notification>

        <!-- Show a full page loader while initial check is being done -->
        <FullPageLoader v-if="!initialCheckDone" :isLoading="true" />

        <!-- Show login box if not logged in and initial check is done -->
        <div v-if="!loggedIn && initialCheckDone">
            <LoginBox
                @setLoading="setLoading"
                @setAuthenticating="setAuthenticating"
                @setLoggedIn="setLoggedIn"
                @loginSuccess="handleLoginSuccess"
                :loading="loading"
                :authenticating="authenticating"
                :key="loginBoxKey"
            />
        </div>

        <div v-else>
            <FullPageLoader v-if="loading" :isLoading="true"/>

            <div v-if="isInvoicesOverview">
                <InvoiceExportModal
                    ref="InvoiceExportModal"
                    :passDateRange="dateRange"
                />
            </div>

            <SideNav
                :user="user"
                ref="SideNav"
            />

            <main>
                <div class="topbar"></div>

                <div class="row" v-if="environment == 'demo' || (environment == 'test' && $route.name == 'Dashboard')">
                    <div class="column">
                        <b-message title="Test/demo omgeving" aria-close-label="Sluiten" type="is-info">
                            Je bekijkt momenteel de {{ environment }} omgeving van PlanitEasy.nl, de getoonde data is fictief en bedoeld om mee te testen. E-mails welk normaal gesproken naar de klanten verstuurd worden, worden nu verstuurd naar {{ environment == 'test' ? 'info@planiteasy.nl' : 'de organisatie eigenaar' }}. Enkele functionaliteiten zijn uitgeschakeld.
                        </b-message>
                    </div>
                </div>

                <div class="row" v-if="!initialized">
                    <div class="column">
                        <b-message type="is-warning">Bezig met opstarten...</b-message>
                    </div>
                </div>

                <div class="row" v-if="showFilterBar">
                    <div class="column">
                        <div id="filterbar">
                            <div class="filterbar-left">
                                <div class="orgfilter" v-if="showOrgFilter" :key="'orgfilter-' + dataBelongsTo + Object.keys(organisations).length">
                                    <b-field label="Toon data van">
                                        <b-select v-model="dataBelongsTo" class="databelongsto">
                                            <option value="all">Alle (organisaties)</option>
                                            <option
                                                v-for="(organisation, id) in organisations"
                                                :value="id"
                                                :key="id"
                                            >({{ organisation.short_name }}) {{ organisation.name }} (#{{ organisation.id }})
                                            </option>
                                        </b-select>
                                    </b-field>
                                </div>

                                <div v-if="hasDateRangeFilter" class="daterange">
                                    <b-field :label="dateRangeFilterLabel">
                                        <b-datepicker
                                            placeholder="Klik om te selecteren"
                                            v-model="dateRange"
                                            :nearby-selectable-month-days="true"
                                            clearable
                                            range
                                        >
                                            <b-button
                                                label="Geen"
                                                type="is-danger"
                                                icon-left="times"
                                                outlined
                                                @click="dateRange = null"
                                            />
                                            <b-button
                                                label="Huidige maand"
                                                type="is-primary"
                                                icon-left="calendar-alt"
                                                outlined
                                                @click="setDateRangeThisMonth"
                                            />
                                        </b-datepicker>
                                    </b-field>
                                </div>

                                <div v-if="hasStatusFilter" class="statusfilter">
                                    <b-field label="Status">
                                        <b-dropdown
                                            aria-role="list"
                                            :triggers="['click']"
                                            v-model="isStatusDropdownActive"
                                            :can-close="['outside']"
                                        >
                                            <template #trigger>
                                                <b-button icon-right="chevron-down" :class="selectedFilters.length === 1 ? selectedFilters[0].replace('+', '') : ''">
                                                    <span v-if="selectedFilters.length === possibleFilters.length">Filter op status (alle)</span>
                                                    <span v-else-if="selectedFilters.length === 1">{{ selectedFilters[0] === 'none' ? 'Geen' : possibleFilters.find(f => f.name === selectedFilters[0]).label }}</span>
                                                    <span v-else>Filter op status: <b-tag>{{ selectedFilters.length }}</b-tag></span>
                                                </b-button>
                                            </template>

                                            <div class="statusfilter-dropdown" :key="'dropdown-content-' + selectedFilters.length + '-' + selectedFilters.join(',')">
                                                <b-dropdown-item custom class="statusfilter-toggles">
                                                    <b-button size="is-small" expanded type="is-light" @click="handleFilterClick('all')" :disabled="selectedFilters.length === possibleFilters.length">Alle</b-button>
                                                    <b-button size="is-small" expanded type="is-light" @click="handleFilterClick('none')" :disabled="selectedFilters.length === 0">Geen</b-button>
                                                </b-dropdown-item>

                                                <b-dropdown-item separator />

                                                <b-dropdown-item
                                                    aria-role="listitem"
                                                    v-for="filter in possibleFilters"
                                                    :key="filter.name"
                                                    custom
                                                    @click.stop.prevent
                                                >
                                                    <label
                                                        class="b-checkbox checkbox"
                                                        :class="filter.name.replaceAll('+', 'plusreserved')"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            :checked="selectedFilters.includes(filter.name)"
                                                            @click.stop="handleFilterClick(filter.name)"
                                                        >
                                                        <span class="check"></span>
                                                        <span class="control-label">{{ filter.label }}</span>
                                                        <span
                                                            class="status-color"
                                                            :class="filter.name.replaceAll('+', '')"
                                                        ></span>
                                                    </label>
                                                </b-dropdown-item>
                                            </div>
                                        </b-dropdown>
                                    </b-field>
                                </div>

                                <div v-if="hasInvoiceExportButton" class="exportbtn">
                                    <b-field label="Transacties"></b-field>
                                    <b-button type="is-primary" @click="$refs.InvoiceExportModal.toggleExportModal()">Exporteren</b-button>
                                </div>

                                <div v-if="hasActiveSwitch" class="activeswitch">
                                    <b-field label="Toon autorisaties"></b-field>

                                    <b-field>
                                        <b-radio-button v-model="showActive" native-value="all" type="is-primary">
                                            <span>Alles</span>
                                        </b-radio-button>
                                        <b-radio-button v-model="showActive" :native-value="true" type="is-success">
                                            <span>Actief</span>
                                        </b-radio-button>
                                        <b-radio-button v-model="showActive" :native-value="false" type="is-danger">
                                            <span>Verlopen</span>
                                        </b-radio-button>
                                    </b-field>
                                </div>
                            </div>

                            <div class="filterbar-right">
                                <div class="sensitive-data pull-right text-right">
                                    <b-field label="Gevoelige data">
                                        <b-tooltip :label="hideSensitiveData ? 'Toon gevoelige data' : 'Verberg gevoelige data'" position="is-left">
                                            <b-button :type="hideSensitiveData ? 'is-success' : 'is-danger'" :icon-right="hideSensitiveData ? 'eye' : 'eye-slash'" @click="$store.dispatch('toggleSensitiveData')"></b-button>
                                        </b-tooltip>
                                    </b-field>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="user && user.temp_pass">
                    <ActivateAccount />
                </div>
                <div v-else>
                    <router-view :key="$route.path + '-' + loginBoxKey" />
                </div>

            </main>
        </div>
    </div>
</template>

<script>
import LoginBox from './LoginBox'
import SideNav from './SideNav'
import ActivateAccount from './users/ActivateAccount.vue'
import InvoiceExportModal from './invoices/components/InvoiceExportModal'
import statusFiltersMixin from '@/mixins/statusFiltersMixin'

export default {
    name: 'ThePanel',

    mixins: [statusFiltersMixin],

    components: {
        LoginBox,
        SideNav,
        ActivateAccount,
        InvoiceExportModal
    },
        data () {
        return {
            allCustomers: {},
            dateRange: null,
            statusFilter: 'all',

            loggedIn: false,
            loading: false, // General loading state for UI operations
            authenticating: false, // Specific state for authentication process
            showActive: true,
            isStatusDropdownActive: false,
            firstTimeLogin: true, // Track if this is the first login in the session
            initialCheckDone: false, // Track if initial auth check is done
            loginBoxKey: 0, // Key for forcing LoginBox component refresh
            refreshKey: 0, // Key for forcing entire component refresh
            tokenRefreshInterval: null // Interval for token refresh checks
        }
    },

    beforeDestroy() {
        // Clean up the token refresh interval
        if (this.tokenRefreshInterval) {
            clearInterval(this.tokenRefreshInterval)
        }
    },

    created() {
        // Check for saved user data in localStorage
        const savedToken = localStorage.getItem('auth_token')
        const savedUser = localStorage.getItem('user_data')

        if (savedToken && savedUser) {
            // IMPORTANT: First refresh the token before initializing the app
            // This ensures we have a valid token before making any API calls
            this.refreshTokenBeforeInit(savedToken, savedUser)
        } else {
            // No saved credentials, mark initialization as done
            this.initialCheckDone = true
        }
    },

    mounted() {
        // Listen for loginSuccess event on $root
        this.$root.$on('loginSuccess', (token, userData) => {
            this.handleLoginSuccess(token, userData)
        })

        // Set up token refresh on short idle periods
        // This is separate from the main IdleVue timeout
        this.setupTokenRefreshTimer()
    },

    computed: {
        cssVars() {
            return {
                '--color1': this.user.organisation_color1,
                '--color2': this.user.organisation_color2
            }
        },

        initialized() {
            return this.$store.state.initialized || false
        },

        user() {
            return this.$store.state.user || {}
        },

        organisations() {
            return this.$store.state.organisations || {}
        },

        showOrgFilter(){
            return this.user && this.user?.role === 'pit' && (this.organisations && Object.keys(this.organisations).length > 1)
        },

        showFilterBar(){
            return _.every([
                !!this.filterBarPossible,
                this.user.role == 'pit' || this.hasDateRangeFilter || this.hasStatusFilter
            ])
        },

        dateRangeFilterLabel(){
            return _.has(this.$route.meta, 'dateRangeFilterLabel') ? this.$route.meta.dateRangeFilterLabel : 'Selecteer datumbereik'
        },

        hasDateRangeFilter(){
            return !!_.has(this.$route.meta, 'dateRangeFilter')
        },

        showEscalation(){
            return !!this.user.organisation_settings?.escalation == 1 || this.user.role == 'pit'
        },

        hasStatusFilter(){
            return !!_.has(this.$route.meta, 'statusFilter')
        },

        hasInvoiceExportButton(){
            return !!_.has(this.$route.meta, 'hasInvoiceExportButton') && (this.$route.path == '/panel/facturen/recent' || this.$route.path == '/panel/creditfacturen')
        },

        hasActiveSwitch(){
            return _.has(this.$route.meta, 'activeSwitch') && !!this.$route.meta.activeSwitch
        },

        filterBarPossible() {
            return _.includes([
                'AccountsOverview',
                'CommunicationOverview',
                // 'LogsOverview',
                'Dashboard',
                'MailOverview',
                'BoxesOverview',
                'ProductsOverview',
                'ContractsOverview',
                'ContractsTemplates',
                'ContractsPriceChanges',
                'InvoicesOverview',
                'CreditInvoiceOverview',
                'CustomersOverview',
                'ConnectOverview',
                'OnlineRentalSettings',
                'DiscountsOverview'
            ], this.$route.name)
        },

        hideSensitiveData() {
            return this.$store.state.hideSensitiveData
        },

        isIdle() {
            return this.$store.state.idleVue.isIdle
        },

        environment() {
            return this.$store.state.env
        },

        isInvoicesOverview() {
            return this.$route.name == 'InvoicesOverview' || this.$route.name == 'CreditInvoiceOverview'
        },

        dataBelongsTo: {
            get() {
                return this.$store.state.dataBelongsTo
            },
            set(value) {
                this.$store.dispatch('setDataBelongsTo', value)
            }
        }
    },

    methods: {
        setLoading(value) {
            this.loading = value
        },

        setAuthenticating(value) {
            this.authenticating = value
        },

        /**
         * Initialize the application with user data
         * This function can be called both from created hook and after login
         */
        initializeWithUserData(token, userData) {
            try {
                // Parse user data if it's a string
                const parsedUserData = typeof userData === 'string' ? JSON.parse(userData) : userData

                // Set the token and user data in the store
                this.$store.commit('setToken', token)
                this.$store.commit('setUser', parsedUserData)

                // Set the user as logged in immediately
                this.loggedIn = true

                // Set initialized to true to show the panel
                this.$store.commit('setInitialized', true)

                // Set the organization if available
                if (parsedUserData.organisation) {
                    this.$store.dispatch('setDataBelongsTo', parsedUserData.organisation)
                }

                // Explicitly reset dateRange to null
                this.$store.dispatch('setDateRange', null)
                this.dateRange = null

                // Load organizations if available
                const savedOrgs = localStorage.getItem('organisations')
                if (savedOrgs) {
                    try {
                        this.$store.commit('setOrganisations', JSON.parse(savedOrgs))
                    } catch (e) {
                            console.error('Error parsing saved organisations:', e)
                        }
                }

                // Refresh user data in the background
                this.$store.dispatch('refreshUserData')
                    .catch(error => {
                        // If refresh fails (e.g., token expired), log out the user
                        if (error.response && error.response.status === 401) {
                            this.$store.commit('clearUserData')
                            this.loggedIn = false
                            window.location.href = '/logout/force?expired=true'
                        }
                    })

                return true
            } catch (e) {
                console.error('Error initializing with user data:', e)
                // Clear invalid data
                localStorage.removeItem('user_data')
                return false
            }
        },

        handleLoginSuccess(token, userData) {
            // Use passed parameters if available, otherwise try localStorage
            const authToken = token || localStorage.getItem('auth_token')
            const userDataValue = userData || localStorage.getItem('user_data')

            if (authToken && userDataValue) {
                // Initialize with the provided or stored user data
                this.initializeWithUserData(authToken, userDataValue)

                // Make sure loggedIn is set to true
                this.loggedIn = true

                // Increment refresh key to force component update
                this.refreshKey++

                // Force component update
                this.$forceUpdate()

                // Use nextTick to ensure DOM is updated
                this.$nextTick(() => {
                    // Increment refresh key again to ensure update
                    this.refreshKey++

                    // Force update again
                    this.$forceUpdate()
                })
            } else {
                console.error('No token or user data available in handleLoginSuccess')
            }

            // Make sure loading is turned off
            this.loading = false
        },

        setLoggedIn(value){
            this.loggedIn = value

            if(value){
                document.getElementsByTagName("body")[0].classList.remove('login')
            } else {
                document.getElementsByTagName("body")[0].classList.add('login')
            }

            if (!!this.loggedIn) {
                this.checkSensitiveData()

                // Make sure loading is turned off
                this.loading = false

                // Increment refresh key to force component update
                this.refreshKey++

                // Force component update when logged in
                this.$forceUpdate()

                // Ensure router view is updated
                this.$nextTick(() => {
                    // Make sure the app is initialized
                    this.$store.commit('setInitialized', true)

                    // Increment refresh key again to ensure update
                    this.refreshKey++

                    // Force update again
                    this.$forceUpdate()

                    // Use setTimeout to ensure the component is fully updated
                    setTimeout(() => {
                        // Increment refresh key one more time
                        this.refreshKey++

                        // Force update again
                        this.$forceUpdate()
                    }, 100)
                })
            }

            setTimeout(() => {
                this.$store.dispatch('setAuthRedirected', true)
            }, 3000)
        },

        setDateRangeThisMonth(){
            const thisMonth = [moment(new Date()).startOf('month')._d, moment(new Date()).endOf('month')._d]
            this.$store.dispatch('setDateRange', thisMonth)
            this.dateRange = thisMonth
        },

        /**
         * Set up a timer to periodically check for user activity and refresh token
         * This is separate from the IdleVue timeout
         */
        setupTokenRefreshTimer() {
            // Check for inactivity every 10 minutes
            this.tokenRefreshInterval = setInterval(() => {
                if (!this.loggedIn) return

                // Get the last user activity time
                const lastActivity = this.getLastActivityTime()
                const now = Date.now()
                const inactiveTime = now - lastActivity

                // Check if we've refreshed the token recently
                const lastRefresh = this.$store.state.lastTokenRefresh
                const refreshedRecently = lastRefresh && (now - lastRefresh < 5 * 60 * 1000) // 5 minutes

                // If inactive for more than 15 minutes but less than the IdleVue timeout,
                // and we haven't refreshed recently, refresh the token silently
                if (inactiveTime > 15 * 60 * 1000 && inactiveTime < 50 * 60 * 1000 && !refreshedRecently) {
                    console.log('[🔄] User inactive for 15+ minutes, refreshing token silently')
                    this.$store.dispatch('attemptTokenRefresh', true) // Silent refresh
                }
            }, 10 * 60 * 1000) // Check every 10 minutes
        },

        /**
         * Get the last user activity time
         */
        getLastActivityTime() {
            // Try to get from IdleVue if available
            if (this.$store.state.idleVue && this.$store.state.idleVue.lastActive) {
                return this.$store.state.idleVue.lastActive
            }

            // Fallback to now
            return Date.now()
        },

        refreshTokenBeforeInit(savedToken, savedUser) {
            console.log('[🔄] Refreshing token before initializing app')

            // Show loading state
            this.loading = true

            // First, set the token in the store so the refresh request can use it
            this.$store.commit('setToken', savedToken)

            // Attempt to refresh the token in silent mode (no toasts)
            this.$store.dispatch('attemptTokenRefresh', true)
                .then(success => {
                    console.log('[✅] Token refresh before init:', success ? 'success' : 'skipped')

                    // Initialize the app with user data
                    this.initializeWithUserData(
                        localStorage.getItem('auth_token') || savedToken,
                        savedUser
                    )

                    // Mark initialization as done
                    this.initialCheckDone = true
                    this.loading = false
                })
                .catch(error => {
                    console.error('[❌] Failed to refresh token before init:', error)

                    // Even if refresh failed, we should still try to initialize
                    // The token might still be valid, and if not, the app will handle it
                    this.initializeWithUserData(savedToken, savedUser)

                    // We won't try to refresh again immediately to avoid blacklisting
                    // Instead, we'll let the normal refresh mechanism handle it if needed
                    console.log('[🔄] Not attempting second token refresh to avoid blacklisting')

                    // Mark initialization as done
                    this.initialCheckDone = true
                    this.loading = false
                })
        },

        checkSensitiveData() {
            if(this.user && this.user.settings) {
                let settings = this.user.settings

                // If settings is a string, try to parse it
                if (typeof settings === 'string') {
                    try {
                        settings = JSON.parse(settings)
                    } catch (e) {
                        // Use default if parsing fails
                        this.$store.dispatch('toggleSensitiveData', true)
                        return
                    }
                }

                if (_.has(settings, 'sensitivedata')) {
                    const sensitivedata = settings.sensitivedata
                    const shouldHide = sensitivedata === 1  // 1 = privacy ON (hide), 0 = privacy OFF (show)
                    this.$store.dispatch('toggleSensitiveData', shouldHide)
                } else {
                    // If sensitivedata setting is not present, use default (privacy ON = hide sensitive data)
                    this.$store.dispatch('toggleSensitiveData', true)
                }
            } else {
                // If no user or settings, use default (privacy ON = hide sensitive data)
                this.$store.dispatch('toggleSensitiveData', true)
            }
        },

    },

    watch: {
        '$store.state.dataBelongsTo': function(newVal, oldVal){
            if(newVal !== oldVal){
                this.dataBelongsTo = newVal
            }
        },

        dateRange(newVal){
            // Only set dateRange in store if it has a value
            // This prevents sending empty date ranges in API calls
            if (newVal && Array.isArray(newVal) && newVal.length > 0) {
                this.$store.dispatch('setDateRange', newVal)
            } else {
                // If null or empty, explicitly set to null
                this.$store.dispatch('setDateRange', null)
            }
        },

        showActive(newVal) {
            this.$store.dispatch('setShowActive', newVal)
        },

        isIdle(newVal){
            if(newVal === true) {
                if(this.loggedIn){
                    console.log('[⏰] User is now idle')

                    // IMPORTANT: When IdleVue triggers idle=true, we should log the user out
                    // This is the main idle timeout that should force logout

                    // Show notification
                    if (this.$buefy && this.$buefy.toast) {
                        this.$buefy.toast.open({
                            message: 'Je bent automatisch uitgelogd wegens inactiviteit.',
                            type: 'is-danger',
                            position: 'is-top',
                            duration: 5000,
                            queue: false
                        })
                    }

                    // Clear user data and redirect to logout
                    this.$store.commit('clearUserData')
                    setTimeout(() => {
                        window.location = '/logout/force?timedout=true'
                    }, 1000)
                }
            } else if (newVal === false && this.loggedIn) {
                // User is active again, reset IdleVue timer
                this.$root.$emit('idle:reset')

                // Also refresh the token if needed
                const lastRefresh = this.$store.state.lastTokenRefresh
                const REFRESH_AFTER_ACTIVITY = 300000 // 5 minutes

                if (!lastRefresh || (Date.now() - lastRefresh > REFRESH_AFTER_ACTIVITY)) {
                    console.log('[🔄] User is active again after a while, refreshing token')
                    this.$store.dispatch('attemptTokenRefresh', true) // Silent refresh
                }
            }
        },

        showEscalation(newVal){
            if(newVal === true){
                this.addEscalationFilters()
            } else {
                this.possibleFilters = this.possibleFilters.filter(filter => filter.name !== 'escalated' && filter.name !== 'escalated_deurwaarder')
            }
        },

        // Watch for user changes to ensure sensitive data is set correctly when settings are loaded
        user: {
            handler(newUser) {
                if (newUser && newUser.settings) {
                    let settings = newUser.settings

                    // If settings is a string, try to parse it
                    if (typeof settings === 'string') {
                        try {
                            settings = JSON.parse(settings)
                        } catch (e) {
                            return
                        }
                    }

                    if (_.has(settings, 'sensitivedata')) {
                        this.checkSensitiveData()
                    } else {
                    }
                } else {
                }
            },
            deep: true
        },

        // We can't watch events with the watch option, so we'll handle this in mounted
    }
}
</script>

<style lang="scss">
    $color1: var(--color1);
    $color2: var(--color2);

    #panel {
        a:not(.button, .dropdown-item, .menu-list li a, .tag) {
            color: $color1;

            &:hover {
                /* darken() only works on Sass colors; --color1 is runtime — use color-mix */
                color: color-mix(in srgb, var(--color1) 90%, black);
            }
        }
    }

    #panel main .topbar {
        background-color: $color1;
    }
</style>
